import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { BookOpen, ChevronRight, Play, Star, CheckCircle2 } from "lucide-react";

/**
 * "Minha trilha" — compact horizontal carousel displayed above the
 * Journey timeline on Home. Lists the student's favourited lessons +
 * any lesson currently in-progress (0 < progress < 100). Links each
 * card to `/biblioteca?lesson=<id>` so the viewer opens with the
 * iframe already queued up.
 */
export default function LessonTrailCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/api/library/me/trail");
      setItems(res.data?.items || []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return null;
  if (!items || items.length === 0) return null;

  return (
    <div
      data-testid="lesson-trail-card"
      className="rounded-xl border border-[#282D3D] bg-[#14161C] p-4 mb-3"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#FF6B00]/15 border border-[#FF6B00]/40 flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-[#FF6B00]" />
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-widest font-extrabold text-[#FF6B00]">
              Minha trilha
            </span>
            <div className="text-[11px] text-gray-400">
              {items.length} {items.length === 1 ? "aula" : "aulas"} salvas · continue de onde parou
            </div>
          </div>
        </div>
        <Link
          to="/biblioteca"
          data-testid="trail-see-all"
          className="hidden sm:inline-flex items-center gap-0.5 text-[11px] font-semibold text-gray-400 hover:text-[#FF6B00]"
        >
          Ver biblioteca <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div
        data-testid="trail-scroll"
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 -mx-1 px-1"
        style={{ scrollbarWidth: "thin" }}
      >
        {items.map((lesson) => {
          const progress = Math.max(0, Math.min(100, Number(lesson.progress || 0)));
          const isDone = !!lesson.completed || progress >= 100;
          return (
            <Link
              key={lesson.id}
              to={`/biblioteca?lesson=${encodeURIComponent(lesson.id)}`}
              data-testid={`trail-item-${lesson.id}`}
              className="shrink-0 w-[220px] sm:w-[240px] snap-start bg-[#101319] border border-[#232733] rounded-lg overflow-hidden hover:border-[#FF6B00]/50 transition-colors"
            >
              <div
                className="relative w-full bg-[#0B0D12]"
                style={{ aspectRatio: "16 / 9" }}
              >
                {lesson.thumbnail_url ? (
                  <img
                    src={lesson.thumbnail_url}
                    alt={lesson.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-90"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1A1D26] to-[#0B0D12]">
                    <BookOpen className="w-8 h-8 text-[#3E4760]" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FF6B00] text-black font-extrabold text-[10px] uppercase tracking-wider">
                    <Play className="w-3 h-3 fill-black" />
                    {isDone ? "Rever" : progress > 0 ? "Continuar" : "Assistir"}
                  </span>
                </div>
                {lesson.favorite && (
                  <span
                    data-testid={`trail-fav-${lesson.id}`}
                    className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-500/95 text-black"
                  >
                    <Star className="w-3.5 h-3.5 fill-black" />
                  </span>
                )}
                {isDone && (
                  <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500 text-black text-[9px] font-extrabold uppercase tracking-wider">
                    <CheckCircle2 className="w-3 h-3" /> Concluída
                  </span>
                )}
                {progress > 0 && !isDone && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/40">
                    <div
                      className="h-full bg-[#FF6B00]"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                )}
              </div>
              <div className="p-2.5">
                <h4 className="text-xs font-extrabold text-white leading-snug line-clamp-2">
                  {lesson.title}
                </h4>
              </div>
            </Link>
          );
        })}
      </div>

      <Link
        to="/biblioteca"
        data-testid="trail-see-all-mobile"
        className="sm:hidden mt-2 inline-flex items-center justify-end gap-0.5 w-full text-[11px] font-semibold text-gray-400 hover:text-[#FF6B00]"
      >
        Ver biblioteca <ChevronRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}
